/**
 * Rate Limit Card Component
 *
 * Displays Reddit API rate limit status including usage percentage, remaining requests,
 * and reset time with color-coded health indicators.
 */

import React, { useState, useEffect } from 'react';
import { Paper, Title, Group, Stack, Text, Badge, Loader, Progress, Alert } from '@mantine/core';
import { IconGauge, IconClock, IconInfoCircle } from '@tabler/icons-react';
import type { RateLimitSection } from '../../../../domain/entities/AdminDashboard';

interface RateLimitCardProps {
  rateLimit: RateLimitSection | null;
  isLoading?: boolean;
}

/**
 * Maps rate limit status to appropriate progress color
 */
function getProgressColor(status: string): string {
  switch (status) {
    case 'Optimal':
      return 'green';
    case 'Healthy':
      return 'blue';
    case 'Warning':
      return 'red';
    default:
      return 'gray';
  }
}

/**
 * Formats time until rate limit reset
 */
function formatTimeUntilReset(resetTime: number): string {
  const now = Math.floor(Date.now() / 1000); // Current time in Unix seconds
  const diffSeconds = resetTime - now;

  if (diffSeconds <= 0) {
    return 'Resetting...';
  }

  if (diffSeconds < 60) {
    return `${diffSeconds}s`;
  } else if (diffSeconds < 3600) {
    const minutes = Math.floor(diffSeconds / 60);
    return `${minutes}m`;
  } else {
    const hours = Math.floor(diffSeconds / 3600);
    const minutes = Math.floor((diffSeconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }
}

export const RateLimitCard: React.FC<RateLimitCardProps> = ({
  rateLimit,
  isLoading = false
}): React.ReactElement => {
  // State for countdown timer
  const [timeUntilReset, setTimeUntilReset] = useState<string>('');

  // Update countdown every second
  useEffect(() => {
    if (!rateLimit) {
      return;
    }

    const updateCountdown = (): void => {
      setTimeUntilReset(formatTimeUntilReset(rateLimit.resetTime));
    };

    // Initial update
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [rateLimit]);

  // Handle null case (no data yet)
  if (!rateLimit && !isLoading) {
    return (
      <Paper shadow="sm" p="md" radius="md" withBorder>
        <Stack gap="md">
          <Group gap="xs">
            <IconGauge size={24} />
            <Title order={3}>Rate Limit</Title>
          </Group>

          <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
            <Text size="sm">No rate limit data available yet. Data will appear after the first Reddit API call.</Text>
          </Alert>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper shadow="sm" p="md" radius="md" withBorder>
      <Stack gap="md">
        <Group gap="xs">
          <IconGauge size={24} />
          <Title order={3}>Rate Limit</Title>
        </Group>

        {isLoading || !rateLimit ? (
          <Loader size="sm" />
        ) : (
          <>
            <Group justify="space-between">
              <Text size="sm" fw={600}>
                Status:
              </Text>
              <Badge color={getProgressColor(rateLimit.status)} variant="light">
                {rateLimit.status}
              </Badge>
            </Group>

            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="xs" c="dimmed">
                  Usage
                </Text>
                <Text size="sm" fw={700}>
                  {Math.round(rateLimit.usagePercent)}%
                </Text>
              </Group>
              <Progress
                value={rateLimit.usagePercent}
                color={getProgressColor(rateLimit.status)}
                size="lg"
                radius="md"
                striped={rateLimit.status === 'Warning'}
                animated={rateLimit.status === 'Warning'}
              />
            </Stack>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Used:
              </Text>
              <Text size="sm" fw={500}>
                {rateLimit.used}
              </Text>
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Remaining:
              </Text>
              <Text size="sm" fw={500} c="teal">
                {rateLimit.remaining}
              </Text>
            </Group>

            <Group justify="space-between">
              <Group gap={4}>
                <IconClock size={16} />
                <Text size="sm" c="dimmed">
                  Resets in:
                </Text>
              </Group>
              <Text size="sm" fw={500}>
                {timeUntilReset}
              </Text>
            </Group>

            {rateLimit.status === 'Warning' && (
              <Alert icon={<IconInfoCircle size={16} />} color="red" variant="light">
                <Text size="sm">High API usage detected. Crawler may slow down to avoid rate limits.</Text>
              </Alert>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
};
